import { Check, ChevronDown, Filter, Plus, Settings } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { PermissionGate } from '../../auth/PermissionGate';
import { CreateButton } from '../CreateButton';
import { SplitButton } from '../SplitButton';
import { CountBadge } from '../CountBadge';
import { Menu, type MenuItem } from '../Menu';
import { Tooltip } from '../Tooltip';
import { SearchGroup, TOOLBAR_SEARCH_WIDTH } from './SearchGroup';
import type { IndexViewType } from './useIndexViewType';

/**
 * The list toolbar.
 *
 * Layout is a deliberate house override of reference §3's ordering: **create · [search ·
 * filter · search · record count · settings]**. The filter toggle, search, the count, and
 * the settings cog are one connected control — the count is a bordered segment of the input
 * rather than loose text beside it, so the group reads as a single field. The list/table
 * switch lives in the settings menu rather than as its own button.
 */
export function IndexToolbar({
  module,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  total,
  loading,
  activeFilterCount,
  filterRailOpen,
  onToggleFilterRail,
  onCreate,
  createLabel = 'Create',
  createPermission,
  createDisabledReason,
  createMenu,
  shortcutMenu,
  shortcutLabel = 'Shortcuts',
  recordFilters,
  recordActions,
  viewType,
  onViewTypeChange,
  extraViewLabel,
  headerGroups,
  hiddenHeaderGroups = [],
  onToggleHeaderGroup,
  cardGroups,
  hiddenCards = [],
  onToggleCard,
  showDeleted,
  onShowDeletedChange,
  supportsDeleted,
  onOpenHistory,
  onClearData,
}: {
  module: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  total: number;
  loading: boolean;
  activeFilterCount: number;
  filterRailOpen: boolean;
  onToggleFilterRail: () => void;
  onCreate?: () => void;
  /** The button's words. `Create` unless the page makes something the module is not named for. */
  createLabel?: string;
  createPermission?: string;
  createDisabledReason?: string | null;
  createMenu?: MenuItem[];
  shortcutMenu?: MenuItem[];
  shortcutLabel?: string;
  recordFilters?: MenuItem[];
  recordActions?: MenuItem[];
  viewType: IndexViewType;
  onViewTypeChange: (next: IndexViewType) => void;
  /** The page's own third view, by its label — `IndexPageProps.extraView`. Undefined offers two. */
  extraViewLabel?: string;
  /**
   * Column groups the reader can put away. Undefined draws no Headers section at all.
   *
   * All three are optional together, so a toolbar that offers no groups — which is every one
   * but Stock Levels, and the plantilla's own forked shell — passes nothing rather than three
   * empty arguments.
   */
  headerGroups?: { key: string; label: string; keys: string[] }[];
  hiddenHeaderGroups?: string[];
  onToggleHeaderGroup?: (key: string) => void;
  cardGroups?: { key: string; label: string }[];
  hiddenCards?: string[];
  onToggleCard?: (key: string) => void;
  showDeleted: boolean;
  onShowDeletedChange: (next: boolean) => void;
  /**
   * False for a module with no soft-delete axis — an append-only trail has nothing to
   * delete. The entry still renders, disabled and saying why, rather than sitting there
   * looking clickable and doing nothing.
   */
  supportsDeleted: boolean;
  onOpenHistory: () => void;
  /**
   * Opens the shell's Clear data confirmation. Undefined hides the entry entirely — the
   * shell passes it only when the page supplied a handler AND the caller holds
   * `<module>.clear-data`, so a pure permission gate hides rather than disables.
   */
  onClearData?: () => void;
}) {
  const { hasPermission } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  /**
   * How tall the cog's panel may be — the room between the trigger and the bottom of the window.
   *
   * <b>It is MEASURED rather than a viewport fraction</b>, because the panel is positioned
   * against the toolbar and a fraction of the window says nothing about where that is. A
   * `max-h-[70vh]` would be right on one page and clip on the next.
   *
   * <b>The menu grew past the screen the day it gained a section</b>, which is the shape to
   * expect from an unbounded dropdown: View and Records fit, Headers made it long, and Cards
   * ran it off the bottom — with the entries below simply unreachable and nothing saying so.
   * A cap plus a scroll region is what makes a section cost nothing to add.
   */
  const [settingsMaxHeight, setSettingsMaxHeight] = useState<number | null>(null);

  /*
    History Logs reads the audit trail through the same endpoint as the global page, so it
    needs the same key. The modal would 403 into an error state without it — offering the
    entry at all would be a control that exists only to fail.
  */
  const canViewHistory = hasPermission('audit-logs.read');

  useEffect(() => {
    if (!settingsOpen) return;

    /*
      RE-MEASURED on scroll and resize, not once on open. The panel is absolutely positioned
      against a toolbar that moves with the page, so a height taken at open is wrong the moment
      the reader scrolls — and the gap below is what they would lose.

      `GAP` keeps the panel off the window's own edge, which is what the shadow needs to read as
      a raised surface rather than as something cut off.
    */
    function measure() {
      const trigger = settingsRef.current;
      if (!trigger) return;

      const GAP = 16;
      const room = window.innerHeight - trigger.getBoundingClientRect().bottom - GAP;

      // A FLOOR, so a toolbar near the bottom of a short window still gets a usable panel
      // rather than a sliver. It scrolls either way.
      setSettingsMaxHeight(Math.max(room, 200));
    }

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);

    function onPointerDown(event: MouseEvent) {
      if (!settingsRef.current?.contains(event.target as Node)) {
        setSettingsOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setSettingsOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [settingsOpen]);

  /*
    Two chevrons on one control is a control that cannot say what pressing it does, so the two
    menus are mutually exclusive and the shortcut one is what gives way — `createMenu` IS the
    primary action there, and dropping it would leave the module unable to create anything.
  */
  const splitShortcuts = shortcutMenu && shortcutMenu.length > 0 && !(createMenu && createMenu.length > 0);

  if (import.meta.env.DEV && shortcutMenu?.length && createMenu?.length) {
    console.error(
      `<IndexPage module="${module}"> declares both createMenu and shortcutMenu. One control ` +
        'cannot carry two chevrons, so the shortcut menu is ignored.',
    );
  }

  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-4 py-2.5">
      {createMenu && createMenu.length > 0 ? (
        /*
          A module with more than one thing to create. The same button in the same place, and
          the choice is a menu rather than a question asked after the click — each entry
          carries its own permission and its own disabled reason, so "you cannot register
          here" is said on the entry it applies to.
        */
        <PermissionGate permission={createPermission}>
          <Menu
            id={`${module}-create-menu`}
            items={createMenu}
            align="left"
            trigger={({ toggle }) => (
              /*
                THE SAME BUTTON AS THE PLAIN BRANCH BELOW, and that is the point: two copies of
                `PermissionGate → Tooltip → Button → Plus` thirty lines apart is the duplication
                `<CreateButton>` was extracted to end, and the shell is the last place it is
                allowed to survive. What differs here is the chevron and that the click OPENS
                rather than creates — so the component takes a `trailing` node and nothing else
                moves. The gate is OUTSIDE the `<Menu>` rather than on this button, so a reader
                without the key never mounts the menu at all.
              */
              <CreateButton
                id={`${module}-create`}
                onClick={toggle}
                disabledReason={createDisabledReason}
                trailing={<ChevronDown className="h-4 w-4" aria-hidden="true" />}
              />
            )}
          />
        </PermissionGate>
      ) : onCreate && splitShortcuts ? (
        /*
          ONE primary action, with the surface's other verbs on its trailing edge.

          The chevron is NOT inside the `<PermissionGate>` the plain button below sits in, and
          that is the point of the component rather than an oversight: those entries belong to
          other modules and carry their own keys, so gating them on `createPermission` would
          hide Issuances from a reader entitled to it because they may not register an item.
          `<SplitButton>` gates the ACTION segment alone.
        */
        <SplitButton
          id={`${module}-create`}
          label={createLabel}
          leading={<Plus className="h-4 w-4" />}
          onClick={onCreate}
          actionPermission={createPermission}
          disabledReason={createDisabledReason ?? null}
          menuId={`${module}-shortcuts`}
          menuLabel={shortcutLabel}
          items={shortcutMenu ?? []}
        />
      ) : onCreate ? (
        /*
          The shared button — the same one `<DataTablePanel>` draws, so an index page and a
          panel cannot end up with different Creates. It owns the gate, the disabled reason and
          the glyph; this toolbar owns only where it sits.
        */
        <CreateButton
          id={`${module}-create`}
          onClick={onCreate}
          label={createLabel}
          permission={createPermission}
          disabledReason={createDisabledReason}
        />
      ) : null}

      {/*
        One connected control. `<SearchGroup>` owns the border, the focus ring, and the
        record-count segment; the filter toggle and settings cog are appended as further
        segments of the same field.
      */}
      <SearchGroup
        id={module}
        value={searchValue}
        onChange={onSearchChange}
        placeholder={searchPlaceholder}
        total={total}
        loading={loading}
        // Fills whatever Create leaves — see TOOLBAR_SEARCH_WIDTH for why.
        className={TOOLBAR_SEARCH_WIDTH}
        leading={
          /*
            Filter opens the group, and its border is on its RIGHT — it is the first segment,
            so a left border would draw a second line on the group's own edge, and the rounded
            corner is its to carry.

            At the front because it is the control that narrows the list: the reader reaches
            for it before reading what came back, while the settings cog configures the
            surface and belongs at the far end.
          */
          <Tooltip label={filterRailOpen ? 'Hide filters' : 'Show filters'}>
            <button
              type="button"
              id={`${module}-toggle-filters`}
              onClick={onToggleFilterRail}
              aria-pressed={filterRailOpen}
              aria-label={filterRailOpen ? 'Hide filters' : 'Show filters'}
              className={[
                'flex shrink-0 items-center gap-1.5 rounded-l-lg border-r border-slate-300 px-3 transition',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-200',
                filterRailOpen
                  ? 'bg-slate-100 text-slate-700'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700',
              ].join(' ')}
            >
              <Filter className="h-4 w-4" aria-hidden="true" />
              {activeFilterCount > 0 ? <CountBadge count={activeFilterCount} tone="blue" /> : null}
            </button>
          </Tooltip>
        }
      >
        <div className="relative flex shrink-0 items-stretch" ref={settingsRef}>
          <Tooltip label="Index settings">
            <button
              type="button"
              id={`${module}-settings`}
              onClick={() => setSettingsOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={settingsOpen}
              aria-label="Index settings"
              className="flex items-center rounded-r-lg border-l border-slate-300 px-3 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-200"
            >
              <Settings className="h-4 w-4" aria-hidden="true" />
            </button>
          </Tooltip>

          {settingsOpen ? (
            <div
              role="menu"
              /*
                ONE SCROLL REGION, and `overflow-x-hidden` is stated rather than left to CSS.
                `overflow-y-auto` alone computes the other axis to `auto` as well, so a single
                pixel of width would grow a second scrollbar under the first — the product's
                standing rule for any panel that scrolls.

                `modern-scrollbar` because every scroll region in this product carries it: the
                thumb is drawn on hover and on focus and is invisible at rest.
              */
              className="modern-scrollbar absolute right-0 top-full z-40 mt-1 w-56 overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-raised"
              style={settingsMaxHeight === null ? undefined : { maxHeight: settingsMaxHeight }}
            >
              <p className="px-3 pb-1 pt-1.5 text-[10px] uppercase tracking-wider text-gray-600">
                View
              </p>

              {/* The list/table switch lives here rather than as a toolbar button. */}
              {(
                [
                  { value: 'table' as const, label: 'Table' },
                  { value: 'list' as const, label: 'List' },
                  ...(extraViewLabel ? [{ value: 'extra' as const, label: extraViewLabel }] : []),
                ] satisfies { value: IndexViewType; label: string }[]
              ).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={viewType === option.value}
                  id={`${module}-view-${option.value}`}
                  onClick={() => {
                    onViewTypeChange(option.value);
                    setSettingsOpen(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50"
                >
                  {/*
                    The tick stays: it is the only thing saying which view is active. It is a
                    state indicator, not one of the decorative icons.
                  */}
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                    {viewType === option.value ? (
                      <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
                    ) : null}
                  </span>
                  {option.label}
                </button>
              ))}

              {/*
                HEADERS — the column groups this table can put away, ticked exactly like the
                view above them. Drawn only where a page declares groups: a heading over an
                empty list reads as chrome that failed to load.

                The tick means VISIBLE, which is the reading a reader brings to a checkbox
                beside a name — the stored preference is the inverse, and inverting it here
                rather than in the hook keeps "hidden" the thing that is actually persisted.
              */}
              {headerGroups && headerGroups.length > 0 ? (
                <>
                  <div className="my-1.5 border-t border-slate-200" role="separator" />

                  <p className="px-3 pb-1 pt-1.5 text-[10px] uppercase tracking-wider text-gray-600">
                    Headers
                  </p>

                  {headerGroups.map((group) => (
                    <button
                      key={group.key}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={!hiddenHeaderGroups.includes(group.key)}
                      id={`${module}-header-group-${group.key}`}
                      /*
                        The menu STAYS OPEN, unlike the view switch above it. A reader putting
                        columns away is usually putting several away, and a menu that closed on
                        each one would be reopened three times for one arrangement.
                      */
                      onClick={() => onToggleHeaderGroup?.(group.key)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50"
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                        {hiddenHeaderGroups.includes(group.key) ? null : (
                          <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
                        )}
                      </span>
                      {group.label}
                    </button>
                  ))}
                </>
              ) : null}

              {/*
                CARDS — the chrome above the table a reader can put away, ticked exactly like
                the headers above them and persisted the same way.

                It sits AFTER Headers because that is the order they sit in on screen: the cards
                are above the table and the headers are its own, and a cog that listed them the
                other way round would have the reader working bottom-up.

                The shell does not know what a card IS — it owns the tick and the stored
                preference, and `aboveTable` is handed the hidden set. A page that draws its own
                chrome is the only thing that can say what hiding one means.
              */}
              {cardGroups && cardGroups.length > 0 ? (
                <>
                  <div className="my-1.5 border-t border-slate-200" role="separator" />

                  <p className="px-3 pb-1 pt-1.5 text-[10px] uppercase tracking-wider text-gray-600">
                    Cards
                  </p>

                  {cardGroups.map((card) => (
                    <button
                      key={card.key}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={!hiddenCards.includes(card.key)}
                      id={`${module}-card-${card.key}`}
                      // STAYS OPEN, like the headers above: a reader putting cards away is
                      // usually putting several away.
                      onClick={() => onToggleCard?.(card.key)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50"
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                        {hiddenCards.includes(card.key) ? null : (
                          <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
                        )}
                      </span>
                      {card.label}
                    </button>
                  ))}
                </>
              ) : null}

              {/* Same rule as below: a section owns the divider above it. */}
              <div className="my-1.5 border-t border-slate-200" role="separator" />

              <p className="px-3 pb-1 pt-1.5 text-[10px] uppercase tracking-wider text-gray-600">
                Records
              </p>

              {/*
                Nothing in this system is ever hard-deleted, so every list has deleted rows
                sitting behind it and every list needs a way to see them. It belongs in the
                menu rather than the filter rail because it changes what the list IS, not
                which slice of it you are looking at.
              */}
              {/* Disabled for STATE, so it says why — in the product’s own tooltip, not the OS one. */}
              <Tooltip
                disabledReason={
                  supportsDeleted ? null : 'This list is append-only — nothing here is ever deleted.'
                }
                block
              >
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={showDeleted}
                id={`${module}-show-deleted`}
                disabled={!supportsDeleted}
                onClick={() => {
                  onShowDeletedChange(!showDeleted);
                  setSettingsOpen(false);
                }}
                className={[
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition',
                  supportsDeleted
                    ? 'text-slate-600 hover:bg-slate-50'
                    : 'cursor-not-allowed text-slate-300',
                ].join(' ')}
              >
                {/* The tick is the state indicator, exactly as it is for the view radios. */}
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  {showDeleted && supportsDeleted ? (
                    <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
                  ) : null}
                </span>
                Show deleted
              </button>
              </Tooltip>

              {/*
                The module's own view scopes, beneath Show deleted because they are the same
                KIND of control: each changes what the list is rather than which slice of it is
                showing. Permission-gated entries are hidden, like every other menu item.
              */}
              {recordFilters
                ?.filter((item) => !item.permission || hasPermission(item.permission))
                .map((item) => (
                  <Tooltip key={item.key} disabledReason={item.disabledReason ?? null} block>
                    <button
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={item.checked ?? false}
                      id={`${module}-${item.key}`}
                      disabled={Boolean(item.disabledReason?.trim())}
                      onClick={() => {
                        item.onClick?.();
                        setSettingsOpen(false);
                      }}
                      className={[
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition',
                        item.disabledReason?.trim()
                          ? 'cursor-not-allowed text-slate-300'
                          : 'text-slate-600 hover:bg-slate-50',
                      ].join(' ')}
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                        {item.checked ? (
                          <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
                        ) : null}
                      </span>
                      {item.label}
                    </button>
                  </Tooltip>
                ))}

              {/*
                The divider belongs to the entry below it: without the entry it would be a
                rule under the last item, which reads as a rendering fault.
              */}
              {canViewHistory ? (
                <>
                  {/* A full-width rule at slate-200 — the hairline read as a rendering artefact. */}
                  <div className="my-1.5 border-t border-slate-200" role="separator" />

                  <button
                    type="button"
                    role="menuitem"
                    id={`${module}-history-logs`}
                    onClick={() => {
                      setSettingsOpen(false);
                      onOpenHistory();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-50"
                  >
                    <span className="h-4 w-4 shrink-0" aria-hidden="true" />
                    History Logs
                  </button>
                </>
              ) : null}

              {/*
                The module's own ACTIONS — things that DO something, beside History Logs rather
                than among the view switches above.

                `role="menuitem"`, not `menuitemcheckbox`: an action has no checked state, and
                borrowing the filters' treatment would give it one it can never report. They
                share History Logs' spacer for the same reason that entry has one — the only
                glyph in this menu is the tick, which is a state indicator.

                The rule belongs to the entries below it, so it is drawn only when at least one
                survives the permission filter — otherwise a gated-away group leaves a rule
                separating nothing, which reads as a rendering fault.
              */}
              {(() => {
                const actions = recordActions?.filter(
                  (item) => !item.permission || hasPermission(item.permission),
                );

                return actions?.length ? (
                  <>
                    <div className="my-1.5 border-t border-slate-200" role="separator" />

                    {actions.map((item) => (
                      <Tooltip key={item.key} disabledReason={item.disabledReason ?? null} block>
                        <button
                          type="button"
                          role="menuitem"
                          id={`${module}-${item.key}`}
                          disabled={Boolean(item.disabledReason?.trim())}
                          onClick={() => {
                            setSettingsOpen(false);
                            item.onClick?.();
                          }}
                          className={[
                            'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition',
                            item.disabledReason?.trim()
                              ? 'cursor-not-allowed text-slate-300'
                              : 'text-slate-600 hover:bg-slate-50',
                          ].join(' ')}
                        >
                          <span className="h-4 w-4 shrink-0" aria-hidden="true" />
                          {item.label}
                        </button>
                      </Tooltip>
                    ))}
                  </>
                ) : null;
              })()}

              {/*
                Clear data — last in the menu, under its own rule, and in red.

                It is the only destructive entry here, so it is kept away from the view
                switches nobody thinks twice about and given the colour that says so. It only
                renders for a caller holding `<module>.clear-data`; the shell opens a
                confirmation with a tick, because a menu item cannot be the whole safeguard
                for deleting an entire module.
              */}
              {onClearData ? (
                <>
                  <div className="my-1.5 border-t border-slate-200" role="separator" />

                  <button
                    type="button"
                    role="menuitem"
                    id={`${module}-clear-data`}
                    onClick={() => {
                      setSettingsOpen(false);
                      onClearData();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 transition hover:bg-red-50"
                  >
                    {/*
                      The same empty 4x4 spacer History Logs uses, not an icon. This menu
                      carries no decorative icons — the only glyph in it is the tick, which is
                      a state indicator. Red text is what marks this entry as destructive.
                    */}
                    <span className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Clear data
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </SearchGroup>
    </div>
  );
}
